import { ICON_PROPS, STATE_META, cleanSubject, formatRelative, openEmailInOutlook, postToHost, shortDate, tierColor } from './shared.js';
import { CloseoutCard, MorningBriefBanner, MorningBriefV2Card, PromisesSection } from './briefing.js';
import { useEffect, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api, type ActionInboxItem, type ActionInboxState } from '../../api.js';
import { EmailRowMenu, TriageQueueBanner } from './shared-components.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — action-inbox 5-state + legacy + risk feed,
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

// (ICON_PROPS + STATE_META promoted to shared.tsx — used by 8 panel modules.)

// =========================================================================
// Action Inbox — the new default. "What do I need to do now?" view.
// Six sections, server-clustered, machine-vs-human separated.
// =========================================================================

// Phase 4 — Action Inbox 2.0: 5-state grouping with evidence chips,
// per-item Train, and bulk actions per state.

// Phase 7 — Risk Engine feed: lightweight pass over recent inbound mail,
// surfaces fraud / spoofing / version-conflict signals proactively.
export function RiskFeedCard(): React.JSX.Element | null {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinRiskFeed>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [verdictFilter, setVerdictFilter] = useState<'all' | 'dangerous' | 'suspicious'>(() => {
    try { return (localStorage.getItem('addin_risk_verdict') as 'all' | 'dangerous' | 'suspicious') ?? 'all'; }
    catch { return 'all'; }
  });
  const [signalFilter, setSignalFilter] = useState<string | null>(() => {
    try { return localStorage.getItem('addin_risk_signal'); } catch { return null; }
  });
  useEffect(() => { try { localStorage.setItem('addin_risk_verdict', verdictFilter); } catch { /* ignore */ } }, [verdictFilter]);
  useEffect(() => {
    try {
      if (signalFilter == null) localStorage.removeItem('addin_risk_signal');
      else localStorage.setItem('addin_risk_signal', signalFilter);
    } catch { /* ignore */ }
  }, [signalFilter]);
  const [riskCursor, setRiskCursor] = useState(0);
  const [expanded2, setExpanded2] = useState<Awaited<ReturnType<typeof api.addinRiskFeed>>['items'][number] | null>(null);
  // Triage queue — message_ids pushed to Action Inbox via sessionStorage.
  function pushToTriage(mid: string) {
    try {
      const cur = JSON.parse(localStorage.getItem('addin_triage_queue') ?? '[]') as string[];
      if (!cur.includes(mid)) cur.push(mid);
      localStorage.setItem('addin_triage_queue', JSON.stringify(cur));
      window.dispatchEvent(new Event('ai-outlook:triage-update'));
      notifications.show({ message: 'Pushed to Action Inbox triage queue.' });
    } catch { /* ignore */ }
  }
  // Per-item dismiss for 7 days. Stored as Map<message_id, expiry-ms>.
  const [dismissed, setDismissed] = useState<Record<string, number>>(() => {
    try { return JSON.parse(localStorage.getItem('addin_riskfeed_dismissed') ?? '{}'); }
    catch { return {}; }
  });
  // Per-item pin (counterpart to dismiss) — pinned items always render first.
  const [pinned, setPinned] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('addin_riskfeed_pinned') ?? '[]') as string[]; }
    catch { return []; }
  });
  function togglePin(mid: string) {
    setPinned((prev) => {
      const next = prev.includes(mid) ? prev.filter((x) => x !== mid) : [mid, ...prev];
      localStorage.setItem('addin_riskfeed_pinned', JSON.stringify(next));
      return next;
    });
  }
  const [riskSort, setRiskSort] = useState<'score' | 'recent' | 'verdict'>(() => {
    try { return (localStorage.getItem('addin_risk_sort') as 'score' | 'recent' | 'verdict') ?? 'score'; }
    catch { return 'score'; }
  });
  useEffect(() => { try { localStorage.setItem('addin_risk_sort', riskSort); } catch { /* ignore */ } }, [riskSort]);
  function dismissItem(mid: string) {
    const expiry = Date.now() + 7 * 86400000;
    const next = { ...dismissed };
    // Prune expired entries while we're here.
    for (const k of Object.keys(next)) {
      const exp = next[k];
      if (exp !== undefined && exp < Date.now()) delete next[k];
    }
    next[mid] = expiry;
    localStorage.setItem('addin_riskfeed_dismissed', JSON.stringify(next));
    setDismissed(next);
    notifications.show({ message: 'Hidden for 7 days.' });
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-bind on data change
  useEffect(() => {
    function isTyping() {
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      return tag === 'input' || tag === 'textarea';
    }
    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTyping()) return;
      if (!data || !expanded) return;
      const visible = data.items.filter((i) =>
        (verdictFilter === 'all' || i.verdict === verdictFilter) &&
        (!signalFilter || i.signals.some((s) => s.type === signalFilter))
      );
      if (visible.length === 0) return;
      if (e.shiftKey && (e.key === 'J' || e.key === 'K' || e.key === 'j' || e.key === 'k')) {
        // shift+j/k → risk feed nav (so shift namespace doesn't collide with
        // the search panel's own j/k bindings on the same shortcut layer).
        const idx = Math.min(riskCursor, visible.length - 1);
        if (e.key === 'J' || e.key === 'j') {
          e.preventDefault();
          setRiskCursor(Math.min(visible.length - 1, idx + 1));
        } else {
          e.preventDefault();
          setRiskCursor(Math.max(0, idx - 1));
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [data, expanded, verdictFilter, signalFilter, riskCursor]);

  async function load() {
    setBusy(true);
    try { setData(await api.addinRiskFeed(14, 30, 0)); } catch { /* silent */ }
    finally { setBusy(false); }
  }
  async function loadMore() {
    if (!data) return;
    setLoadingMore(true);
    try {
      const next = await api.addinRiskFeed(14, 30, data.items.length);
      setData({ ...next, items: [...data.items, ...next.items] });
    } catch { /* silent */ }
    finally { setLoadingMore(false); }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount only
  useEffect(() => { load(); }, []);

  if (!data || data.items.length === 0) return null;

  const dangerCount = data.items.filter((i) => i.verdict === 'dangerous').length;
  const suspCount = data.items.filter((i) => i.verdict === 'suspicious').length;
  const headlineColor = dangerCount > 0 ? 'red' : suspCount > 0 ? 'orange' : 'yellow';

  function exportCsv() {
    if (!data) return;
    // Filter-aware: respect current verdict + signal filters.
    const items = data.items.filter((i) =>
      (verdictFilter === 'all' || i.verdict === verdictFilter) &&
      (!signalFilter || i.signals.some((s) => s.type === signalFilter))
    );
    const esc = (s: string | number | null | undefined) => {
      if (s == null) return '';
      const v = String(s);
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };
    const header = ['received_at', 'from', 'subject', 'verdict', 'score', 'top_signal', 'all_signals', 'message_id'].join(',');
    const lines = [
      header,
      ...items.map((it) => [
        it.received_at, it.from_addr, it.subject ?? '', it.verdict, it.score,
        it.signals[0]?.type ?? '', it.signals.map((s) => `${s.type}:${s.severity}`).join('|'),
        it.message_id,
      ].map(esc).join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const tag = [
      verdictFilter !== 'all' ? verdictFilter : null,
      signalFilter ? signalFilter.replace(/[^a-z0-9]/gi, '_') : null,
    ].filter(Boolean).join('-');
    a.download = `risk-feed${tag ? `-${tag}` : ''}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <Card padding="md" radius="md" withBorder
      style={{ borderLeft: `4px solid var(--mantine-color-${headlineColor}-6)` }}>
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <IconShield {...ICON_PROPS} />
          <Text fw={600}>Risk feed — last {data.window_days}d</Text>
          {/* Verdict badges double as filter chips. Click to scope, click again to clear. */}
          <Badge
            color={verdictFilter === 'all' ? 'gray' : 'gray'}
            variant={verdictFilter === 'all' ? 'filled' : 'light'}
            style={{ cursor: 'pointer' }}
            onClick={() => setVerdictFilter('all')}
          >
            All {data.items.length}
          </Badge>
          {dangerCount > 0 && (
            <Badge
              color="red"
              variant={verdictFilter === 'dangerous' ? 'filled' : 'light'}
              style={{ cursor: 'pointer' }}
              onClick={() => setVerdictFilter(verdictFilter === 'dangerous' ? 'all' : 'dangerous')}
            >
              {dangerCount} dangerous
            </Badge>
          )}
          {suspCount > 0 && (
            <Badge
              color="orange"
              variant={verdictFilter === 'suspicious' ? 'filled' : 'light'}
              style={{ cursor: 'pointer' }}
              onClick={() => setVerdictFilter(verdictFilter === 'suspicious' ? 'all' : 'suspicious')}
            >
              {suspCount} suspicious
            </Badge>
          )}
        </Group>
        <Group gap={4}>
          <Button size="compact-xs" variant="subtle" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Collapse' : `Show ${data.items.length}`}
          </Button>
          <Button size="compact-xs" variant="subtle" leftSection={<IconFileText size={12} />} onClick={exportCsv}>
            CSV
          </Button>
          <Button size="compact-xs" variant="default" onClick={load} loading={busy}>
            Refresh
          </Button>
        </Group>
      </Group>
      {expanded && (() => {
        // Top signal types across the loaded items, for the chip strip.
        const sigCounts = new Map<string, number>();
        for (const it of data.items) {
          for (const s of it.signals) sigCounts.set(s.type, (sigCounts.get(s.type) ?? 0) + 1);
        }
        const topSignals = [...sigCounts.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6)
          .map(([type, count]) => ({ type, count }));

        const verdictMatch = (i: typeof data.items[number]) =>
          verdictFilter === 'all' || i.verdict === verdictFilter;
        const signalMatch = (i: typeof data.items[number]) =>
          !signalFilter || i.signals.some((s) => s.type === signalFilter);
        const dismissMatch = (i: typeof data.items[number]) => {
          const exp = dismissed[i.message_id];
          return !exp || exp < Date.now();
        };
        const visible = data.items
          .filter((i) => verdictMatch(i) && signalMatch(i) && dismissMatch(i))
          .sort((a, b) => {
            const ap = pinned.includes(a.message_id) ? 0 : 1;
            const bp = pinned.includes(b.message_id) ? 0 : 1;
            if (ap !== bp) return ap - bp;
            if (riskSort === 'recent') return b.received_at.localeCompare(a.received_at);
            if (riskSort === 'verdict') {
              const order = (v: string) => v === 'dangerous' ? 0 : v === 'suspicious' ? 1 : 2;
              const o = order(a.verdict) - order(b.verdict);
              return o !== 0 ? o : b.score - a.score;
            }
            return b.score - a.score;
          });

        return (
        <Stack gap="xs">
          <Group justify="flex-end">
            <Select
              size="xs"
              w={150}
              value={riskSort}
              onChange={(v) => v && setRiskSort(v as 'score' | 'recent' | 'verdict')}
              data={[
                { value: 'score', label: 'Highest score' },
                { value: 'recent', label: 'Most recent' },
                { value: 'verdict', label: 'Verdict severity' },
              ]}
            />
          </Group>
          {topSignals.length > 0 && (
            <Group gap={4} wrap="wrap">
              <Text size="xs" c="dimmed">Signal:</Text>
              <Badge size="xs" variant={signalFilter === null ? 'filled' : 'light'} color="gray"
                style={{ cursor: 'pointer' }} onClick={() => setSignalFilter(null)}>
                Any {data.items.length}
              </Badge>
              {topSignals.map((s) => (
                <Badge
                  key={s.type}
                  size="xs"
                  variant={signalFilter === s.type ? 'filled' : 'light'}
                  color="grape"
                  style={{ cursor: 'pointer', maxWidth: 200 }}
                  onClick={() => setSignalFilter(signalFilter === s.type ? null : s.type)}
                >
                  <Text size="xs" lineClamp={1}>{s.type.replace(/_/g, ' ')} {s.count}</Text>
                </Badge>
              ))}
            </Group>
          )}
          {visible.length === 0 ? (
            <Text size="xs" c="dimmed" p="xs">No items match these filters.</Text>
          ) : (
        <ScrollArea h={Math.min(360, visible.length * 90)}>
          <Stack gap="xs">
            {visible.map((it) => (
              <Paper key={it.message_id} p="xs" withBorder
                style={{ borderLeft: `3px solid var(--mantine-color-${
                  it.verdict === 'dangerous' ? 'red' : it.verdict === 'suspicious' ? 'orange' : 'yellow'
                }-5)`, cursor: 'pointer' }}
                onClick={() => setExpanded2(it)}>
                <Stack gap={4}>
                  <Group gap="xs" wrap="nowrap">
                    <Badge size="xs" color={
                      it.verdict === 'dangerous' ? 'red' : it.verdict === 'suspicious' ? 'orange' : 'gray'
                    }>{it.score}</Badge>
                    <Text size="sm" fw={500} lineClamp={1} style={{ flex: 1, minWidth: 0 }}>{it.subject ?? '(no subject)'}</Text>
                    <Tooltip label={pinned.includes(it.message_id) ? 'Unpin' : 'Pin to top'}>
                      <ActionIcon size="xs" variant={pinned.includes(it.message_id) ? 'filled' : 'subtle'}
                        color="yellow"
                        onClick={(e) => { e.stopPropagation(); togglePin(it.message_id); }}>
                        {pinned.includes(it.message_id) ? <IconStarFilled size={10} /> : <IconStar size={10} />}
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Open in Outlook">
                      <ActionIcon size="xs" variant="subtle"
                        onClick={(e) => { e.stopPropagation(); postToHost({ type: 'open_in_outlook', message_id: it.message_id }); }}>
                        <IconMail size={10} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Push to Action Inbox triage">
                      <ActionIcon size="xs" variant="subtle" color="blue"
                        onClick={(e) => { e.stopPropagation(); pushToTriage(it.message_id); }}>
                        <IconBolt size={10} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Hide for 7 days">
                      <ActionIcon size="xs" variant="subtle" color="gray"
                        onClick={(e) => { e.stopPropagation(); dismissItem(it.message_id); }}>
                        <IconX size={10} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {it.from_addr} · <Tooltip label={new Date(it.received_at).toLocaleString()}>
                      <Text component="span" inherit style={{ borderBottom: '1px dotted var(--mantine-color-gray-5)' }}>
                        {formatRelative(it.received_at)}
                      </Text>
                    </Tooltip>
                    {it.from_cache ? ' · cached scan' : ' · proactive'}
                  </Text>
                  <Group gap={4} wrap="wrap">
                    {it.signals.slice(0, 4).map((s, i) => (
                      <Tooltip key={`${it.message_id}-s-${i}`} label={s.detail}>
                        <Badge
                          size="xs"
                          color={s.severity === 'high' ? 'red' : s.severity === 'medium' ? 'orange' : 'gray'}
                          variant="light"
                          leftSection={<IconAlertTriangle size={9} />}
                        >
                          {s.type.replace(/_/g, ' ')}
                        </Badge>
                      </Tooltip>
                    ))}
                    {it.signals.length > 4 && (
                      <Badge size="xs" variant="dot">+{it.signals.length - 4} more</Badge>
                    )}
                  </Group>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </ScrollArea>
          )}
        </Stack>
        );
      })()}
      {expanded && data.has_more && verdictFilter === 'all' && !signalFilter && (
        <Group justify="center" mt="xs">
          <Button size="compact-xs" variant="default" onClick={loadMore} loading={loadingMore}>
            Load next 30 ({data.total_flagged - data.items.length} remaining)
          </Button>
        </Group>
      )}
      {expanded && (() => {
        const visibleAll = data.items.filter((i) => {
          const dismissedUntil = dismissed[i.message_id];
          return (verdictFilter === 'all' || i.verdict === verdictFilter) &&
            (!signalFilter || i.signals.some((s) => s.type === signalFilter)) &&
            !(dismissedUntil && dismissedUntil >= Date.now());
        });
        if (visibleAll.length === 0) return null;
        return (
          <Group justify="flex-end" mt="xs">
            <Button size="compact-xs" variant="subtle" color="gray"
              onClick={() => {
                if (!confirm(`Hide all ${visibleAll.length} visible items for 7 days?`)) return;
                const next = { ...dismissed };
                const expiry = Date.now() + 7 * 86400000;
                for (const i of visibleAll) next[i.message_id] = expiry;
                localStorage.setItem('addin_riskfeed_dismissed', JSON.stringify(next));
                setDismissed(next);
                notifications.show({ message: `Hidden ${visibleAll.length} items for 7 days.` });
              }}>
              Dismiss all visible
            </Button>
          </Group>
        );
      })()}

      <Modal opened={!!expanded2} onClose={() => setExpanded2(null)} size="lg"
        title={<Group gap="xs"><IconShield {...ICON_PROPS} /><Text fw={600}>Risk details</Text></Group>}>
        {expanded2 && (
          <Stack gap="sm">
            <Group gap="xs" wrap="nowrap">
              <Badge size="lg" color={
                expanded2.verdict === 'dangerous' ? 'red' : expanded2.verdict === 'suspicious' ? 'orange' : 'gray'
              }>{expanded2.verdict.toUpperCase()} ({expanded2.score})</Badge>
              <Text fw={500} lineClamp={2} style={{ flex: 1 }}>{expanded2.subject ?? '(no subject)'}</Text>
            </Group>
            <Text size="xs" c="dimmed">{expanded2.from_addr} · {formatRelative(expanded2.received_at)}</Text>
            <Divider label={`${expanded2.signals.length} signal${expanded2.signals.length === 1 ? '' : 's'}`} labelPosition="left" />
            <Stack gap={4}>
              {expanded2.signals.map((s, i) => (
                <Alert key={`${expanded2.message_id}-s-${i}`}
                  color={s.severity === 'high' ? 'red' : s.severity === 'medium' ? 'orange' : 'gray'}
                  variant="light" p="xs"
                  icon={<IconAlertTriangle size={14} />}>
                  <Stack gap={0}>
                    <Text size="xs" fw={500}>{s.type.replace(/_/g, ' ')}</Text>
                    <Text size="xs">{s.detail}</Text>
                  </Stack>
                </Alert>
              ))}
            </Stack>
            <Group gap={4} justify="flex-end">
              <Button size="xs" variant="default" leftSection={<IconMail size={12} />}
                onClick={() => postToHost({ type: 'open_in_outlook', message_id: expanded2.message_id })}>
                Open in Outlook
              </Button>
              <Button size="xs" color="blue" leftSection={<IconBolt size={12} />}
                onClick={() => { pushToTriage(expanded2.message_id); setExpanded2(null); }}>
                Push to triage
              </Button>
              <Button size="xs" color="gray" variant="light" leftSection={<IconX size={12} />}
                onClick={() => { dismissItem(expanded2.message_id); setExpanded2(null); }}>
                Hide 7d
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Card>
  );
}

export function ActionInbox5StatePanel({
  onOpenSender,
}: {
  onOpenSender?: (email: string) => void;
}): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinActionInbox5State>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [activeState, setActiveState] = useState<ActionInboxState>('act');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [trainFor, setTrainFor] = useState<{ item: ActionInboxItem; current: ActionInboxState } | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Round 28-M Phase 2.3: bulk AI ops result modal.
  const [bulkAiResult, setBulkAiResult] = useState<Awaited<ReturnType<typeof api.addinBulkAi>> | null>(null);
  const [bulkAiBusy, setBulkAiBusy] = useState(false);
  // Round 28-U: right-click context menu on action-inbox rows. Tracks the
  // item + cursor position so we can render a Mantine Menu anchored at the
  // click coords. Closes on item-click and on outside-click.
  const [rowMenu, setRowMenu] = useState<{ item: ActionInboxItem; x: number; y: number } | null>(null);
  async function bulkAi(op: 'summarize' | 'draft_replies' | 'extract_actions') {
    if (selected.size === 0) return;
    setBulkAiBusy(true);
    try {
      const r = await api.addinBulkAi({ message_ids: [...selected], op });
      setBulkAiResult(r);
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'AI op failed' });
    } finally {
      setBulkAiBusy(false);
    }
  }
  // Cursor for keyboard nav within the active state's item list.
  const [cursorIdx, setCursorIdx] = useState(0);
  // Evidence chip filter — show only items whose evidence list contains the chip.
  const [evidenceFilter, setEvidenceFilter] = useState<string | null>(null);
  // One-time tip banner — surfaces shortcuts on first visit; dismissal sticks.
  const [tipDismissed, setTipDismissed] = useState<boolean>(() =>
    localStorage.getItem('addin_action_inbox_tip_dismissed') === '1',
  );

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      setData(await api.addinActionInbox5State());
      setSelected(new Set());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load Action Inbox 2.0');
    } finally {
      setBusy(false);
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: load on mount
  useEffect(() => { load(); }, []);

  // Keyboard nav: 1-5 jump to states, j/k move cursor in active state,
  // Enter opens current item, x toggles selection, t opens train modal.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-bind on data change
  useEffect(() => {
    function isTyping() {
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      return tag === 'input' || tag === 'textarea';
    }
    const stateOrder: ActionInboxState[] = ['act', 'wait', 'know', 'automate', 'ignore'];
    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTyping()) return;
      if (!data) return;
      // 1-5 → state jump
      const numIdx = ['1', '2', '3', '4', '5'].indexOf(e.key);
      if (numIdx >= 0) {
        e.preventDefault();
        setActiveState(stateOrder[numIdx]!);
        setCursorIdx(0);
        clearSel();
        return;
      }
      // ← / → → state cycle (with shift to rebound)
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const cur = stateOrder.indexOf(activeState);
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const next = (cur + dir + stateOrder.length) % stateOrder.length;
        e.preventDefault();
        setActiveState(stateOrder[next]!);
        setCursorIdx(0);
        clearSel();
        return;
      }
      const items = data.items[activeState];
      if (items.length === 0) return;
      const idx = Math.min(cursorIdx, items.length - 1);
      const cur = items[idx]!;
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCursorIdx(Math.min(items.length - 1, idx + 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCursorIdx(Math.max(0, idx - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        postToHost({ type: 'open_in_outlook', message_id: cur.message_id });
      } else if (e.key === 'x') {
        e.preventDefault();
        toggleSel(cur.message_id);
      } else if (e.key === 't') {
        e.preventDefault();
        setTrainFor({ item: cur, current: activeState });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [data, activeState, cursorIdx]);

  function toggleSel(mid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  }

  function selectAllInState() {
    if (!data) return;
    setSelected(new Set(data.items[activeState].map((i) => i.message_id)));
  }
  function clearSel() { setSelected(new Set()); }

  async function bulkAction(action: 'snooze_30d' | 'mute_sender' | 'train_to', trainTo?: ActionInboxState) {
    if (selected.size === 0) return;
    const ids = [...selected];
    setBulkBusy(true);
    try {
      await api.addinActionInboxBulk({
        action,
        message_ids: ids,
        ...(action === 'train_to' && trainTo !== undefined ? { train_to: trainTo } : {}),
        ...(action === 'train_to' ? { shown_in: activeState } : {}),
      });
      notifications.show({
        color: 'green',
        message: `${action.replace('_', ' ')}: ${ids.length} item${ids.length === 1 ? '' : 's'}`,
      });
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Bulk action failed' });
    } finally {
      setBulkBusy(false);
    }
  }

  if (busy && !data) return <Center p="xl"><Loader /></Center>;
  if (err) return (
    <Alert color="red" variant="light">
      {err}
      <Button size="xs" onClick={load} ml="sm">Retry</Button>
    </Alert>
  );
  if (!data) return (
    <Alert color="yellow" variant="light">
      No data yet — index your mailboxes first, or refresh if the brain just came back online.
      <Button size="xs" ml="sm" variant="light" onClick={load}>Refresh</Button>
    </Alert>
  );

  const rawItems = data.items[activeState];
  const items = evidenceFilter
    ? rawItems.filter((it) => it.evidence.some((e) => e === evidenceFilter))
    : rawItems;
  const evCounts = new Map<string, number>();
  for (const it of rawItems) for (const e of it.evidence) evCounts.set(e, (evCounts.get(e) ?? 0) + 1);
  const topEv = [...evCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([e, n]) => ({ e, n }));

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Stack gap={0}>
          <Title order={4}>Action Inbox 2.0</Title>
          <Text size="xs" c="dimmed">
            5-state grouping with per-item evidence. Bulk-train any item that's in the wrong state. (1-5 jump states · j/k navigate · Enter open · x select · t train)
          </Text>
        </Stack>
        <Group gap={4}>
          {data && data.items[activeState].length > 0 && (
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconFileText size={12} />}
              onClick={() => {
                if (!data) return;
                const items = data.items[activeState];
                const esc = (s: string | number | null | undefined) => {
                  if (s == null) return '';
                  const v = String(s);
                  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                };
                const header = ['received_at', 'from', 'subject', 'tier', 'evidence', 'message_id'].join(',');
                const lines = [
                  header,
                  ...items.map((it) => [
                    it.received_at, it.from_addr, it.subject ?? '', it.classification_tier ?? '',
                    it.evidence.join('|'), it.message_id,
                  ].map(esc).join(',')),
                ];
                const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `action-inbox-${activeState}-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}
            >
              CSV ({data.items[activeState].length})
            </Button>
          )}
          <Button size="xs" variant="default" onClick={load} loading={busy}>
            Refresh
          </Button>
        </Group>
      </Group>

      {/* Triage queue from Risk feed pushes — sessionStorage-backed banner */}
      <TriageQueueBanner />

      {/* Phase 7 — Risk feed: surfaces fraud / spoofing / version-conflict signals */}
      <RiskFeedCard />

      {/* One-time keyboard tip — dismiss to localStorage. */}
      {!tipDismissed && (
        <Alert
          color="blue"
          variant="light"
          icon={<IconCommand size={14} />}
          withCloseButton
          onClose={() => {
            setTipDismissed(true);
            localStorage.setItem('addin_action_inbox_tip_dismissed', '1');
          }}
        >
          <Text size="xs">
            <Code>1-5</Code> jump states · <Code>j</Code>/<Code>k</Code> navigate ·
            {' '}<Code>Enter</Code> open · <Code>x</Code> select · <Code>t</Code> train.
            Shortcuts ignore typing in inputs.
          </Text>
        </Alert>
      )}

      <Tabs value={activeState} onChange={(v) => { if (v) { setActiveState(v as ActionInboxState); clearSel(); setCursorIdx(0); } }}>
        <Tabs.List>
          {(Object.keys(STATE_META) as ActionInboxState[]).map((s) => (
            <Tabs.Tab
              key={s}
              value={s}
              leftSection={STATE_META[s].icon}
              rightSection={
                <Badge size="xs" color={STATE_META[s].color} variant={activeState === s ? 'filled' : 'light'}>
                  {data.counts[s]}
                </Badge>
              }
            >
              {STATE_META[s].label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <Group gap="xs" justify="space-between">
        <Text size="xs" c="dimmed">{STATE_META[activeState].hint}</Text>
        <Group gap={4}>
          <Button size="compact-xs" variant="subtle" onClick={selectAllInState} disabled={items.length === 0}>
            Select all ({items.length})
          </Button>
          {/* Bulk archive visible — mute_sender for every visible item */}
          {items.length > 0 && (
            <Button size="compact-xs" variant="subtle" color="orange" leftSection={<IconArchive size={12} />}
              onClick={async () => {
                if (!confirm(`Mute the senders of all ${items.length} visible item${items.length === 1 ? '' : 's'}?`)) return;
                try {
                  await api.addinActionInboxBulk({
                    action: 'mute_sender',
                    message_ids: items.map((i) => i.message_id),
                  });
                  notifications.show({ color: 'orange', message: `Muted ${items.length} sender${items.length === 1 ? '' : 's'}.` });
                  load();
                } catch (e) {
                  notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
                }
              }}>
              Mute all
            </Button>
          )}
          {/* Train every visible item in this state into a target state */}
          {items.length > 0 && (
            <Menu position="bottom-end">
              <Menu.Target>
                <Button size="compact-xs" variant="subtle" color="violet" leftSection={<IconSchool size={12} />}>
                  Train all to…
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {(Object.keys(STATE_META) as ActionInboxState[])
                  .filter((s) => s !== activeState)
                  .map((s) => (
                    <Menu.Item key={s} leftSection={STATE_META[s].icon}
                      onClick={async () => {
                        if (!confirm(`Train all ${items.length} ${STATE_META[activeState].label} items as ${STATE_META[s].label}?`)) return;
                        try {
                          await api.addinActionInboxBulk({
                            action: 'train_to',
                            message_ids: items.map((i) => i.message_id),
                            train_to: s,
                            shown_in: activeState,
                          });
                          notifications.show({ color: 'green', message: `Trained ${items.length} → ${STATE_META[s].label}` });
                          load();
                        } catch (e) {
                          notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
                        }
                      }}>
                      All → {STATE_META[s].label}
                    </Menu.Item>
                  ))}
              </Menu.Dropdown>
            </Menu>
          )}
          {selected.size > 0 && (
            <Button size="compact-xs" variant="subtle" color="gray" onClick={clearSel}>
              Clear ({selected.size})
            </Button>
          )}
        </Group>
      </Group>

      {topEv.length > 0 && (
        <Group gap={4} wrap="wrap">
          <Text size="xs" c="dimmed">Evidence:</Text>
          <Badge size="xs" variant={evidenceFilter === null ? 'filled' : 'light'}
            color={STATE_META[activeState].color}
            style={{ cursor: 'pointer' }} onClick={() => setEvidenceFilter(null)}>
            Any {rawItems.length}
          </Badge>
          {topEv.map((x) => (
            <Badge
              key={x.e}
              size="xs"
              variant={evidenceFilter === x.e ? 'filled' : 'light'}
              color={STATE_META[activeState].color}
              style={{ cursor: 'pointer', maxWidth: 280 }}
              onClick={() => setEvidenceFilter(evidenceFilter === x.e ? null : x.e)}
            >
              <Text size="xs" lineClamp={1}>{x.e} {x.n}</Text>
            </Badge>
          ))}
        </Group>
      )}

      {/* Bulk action bar — appears when items are selected */}
      {selected.size > 0 && (
        <Card padding="xs" radius="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-blue-6)' }}>
          <Group gap="xs" wrap="wrap">
            <Text size="sm" fw={500}>{selected.size} selected:</Text>
            <Button size="compact-xs" leftSection={<IconClock size={12} />} loading={bulkBusy}
              onClick={() => bulkAction('snooze_30d')}>
              Snooze 30d
            </Button>
            <Button size="compact-xs" color="gray" leftSection={<IconArchive size={12} />} loading={bulkBusy}
              onClick={() => bulkAction('mute_sender')}>
              Mute sender
            </Button>
            <Menu position="bottom-start">
              <Menu.Target>
                <Button size="compact-xs" variant="default" leftSection={<IconBrain size={12} />} loading={bulkAiBusy}>
                  AI ops…
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconReport size={14} />} onClick={() => bulkAi('summarize')}>
                  Summarize all
                </Menu.Item>
                <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => bulkAi('draft_replies')}>
                  Draft replies to all
                </Menu.Item>
                <Menu.Item leftSection={<IconFlag size={14} />} onClick={() => bulkAi('extract_actions')}>
                  Extract action items
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <Menu position="bottom-start">
              <Menu.Target>
                <Button size="compact-xs" variant="default" leftSection={<IconSchool size={12} />}>
                  Train as…
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {(Object.keys(STATE_META) as ActionInboxState[])
                  .filter((s) => s !== activeState)
                  .map((s) => (
                    <Menu.Item
                      key={s}
                      leftSection={STATE_META[s].icon}
                      onClick={() => bulkAction('train_to', s)}
                    >
                      Should be in {STATE_META[s].label}
                    </Menu.Item>
                  ))}
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Card>
      )}

      {items.length === 0 ? (
        <Card padding="xl" radius="md" withBorder>
          <Center>
            <Stack align="center" gap={4}>
              <Text fw={600}>Empty</Text>
              <Text size="xs" c="dimmed">Nothing in {STATE_META[activeState].label} right now.</Text>
            </Stack>
          </Center>
        </Card>
      ) : (
        <ScrollArea h={520}>
          <Stack gap={4}>
            {items.map((it, i) => (
              <Paper key={it.message_id} p="xs" withBorder
                onClick={(e) => {
                  // Round 28-U: click row → open the email in Outlook (full
                  // read). Skip when the click landed on an interactive
                  // child (checkbox, train button, sender chip).
                  const target = e.target as HTMLElement;
                  if (target.closest('input, button, [role="button"], a, [data-no-row-click]')) return;
                  postToHost({ type: 'open_in_outlook', message_id: it.message_id });
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setRowMenu({ item: it, x: e.clientX, y: e.clientY });
                }}
                style={{
                  cursor: 'pointer',
                  borderLeft: `3px solid var(--mantine-color-${STATE_META[activeState].color}-5)`,
                  background: i === Math.min(cursorIdx, items.length - 1)
                    ? `var(--mantine-color-${STATE_META[activeState].color}-0)`
                    : undefined,
                  outline: i === Math.min(cursorIdx, items.length - 1)
                    ? `1px solid var(--mantine-color-${STATE_META[activeState].color}-4)`
                    : 'none',
                }}>
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <Checkbox
                    size="xs"
                    checked={selected.has(it.message_id)}
                    onChange={() => toggleSel(it.message_id)}
                    mt={4}
                  />
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    {/* Brain-extracted summary leads when available — that's
                        what the user actually wants to see ("X asks Y to Z"),
                        not "RE: RE: FW:" subject lines. Subject + cleaned
                        prefix become the secondary line. */}
                    {(() => {
                      const summaryEv = it.evidence.find((e) => e.startsWith('Summary: '));
                      const cs = cleanSubject(it.subject);
                      if (summaryEv) {
                        const summary = summaryEv.slice('Summary: '.length);
                        return (
                          <>
                            <Group gap={6} wrap="nowrap" align="flex-start">
                              {it.classification_tier && (
                                <Badge size="xs" color={
                                  it.classification_tier === 'P0' ? 'red' :
                                  it.classification_tier === 'P1' ? 'orange' :
                                  it.classification_tier === 'P2' ? 'yellow' : 'gray'
                                }>{it.classification_tier}</Badge>
                              )}
                              <Text size="sm" fw={500} lineClamp={2} style={{ flex: 1, minWidth: 0 }}>
                                {summary}
                              </Text>
                            </Group>
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {cs.clean}
                            </Text>
                          </>
                        );
                      }
                      return (
                        <Group gap={6} wrap="nowrap">
                          {it.classification_tier && (
                            <Badge size="xs" color={
                              it.classification_tier === 'P0' ? 'red' :
                              it.classification_tier === 'P1' ? 'orange' :
                              it.classification_tier === 'P2' ? 'yellow' : 'gray'
                            }>{it.classification_tier}</Badge>
                          )}
                          {cs.prefix && <Badge size="xs" color="gray" variant="outline">{cs.prefix}</Badge>}
                          {cs.tags.slice(0, 1).map((t) => (
                            <Badge key={t} size="xs" color="grape" variant="light">{t}</Badge>
                          ))}
                          <Text size="sm" fw={500} lineClamp={1} style={{ flex: 1, minWidth: 0 }}>{cs.clean}</Text>
                        </Group>
                      );
                    })()}
                    <Group gap={4} wrap="wrap">
                      <Text
                        size="xs"
                        c="dimmed"
                        data-no-row-click
                        style={{ cursor: onOpenSender ? 'pointer' : 'default' }}
                        onClick={(e) => { e.stopPropagation(); onOpenSender?.(it.from_addr); }}
                      >
                        {it.from_addr} · {formatRelative(it.received_at)}
                      </Text>
                      {/* Round 28-AC: dedup badge — when the brain has
                          collapsed N copies into this row. Action on the row
                          applies to all N copies server-side. */}
                      {it.duplicate_count && it.duplicate_count > 1 ? (
                        <Tooltip label={`${it.duplicate_count} copies of this email — actions apply to all`}>
                          <Badge size="xs" color="gray" variant="light">+{it.duplicate_count - 1} dup</Badge>
                        </Tooltip>
                      ) : null}
                    </Group>
                    {/* Evidence chips — drop the redundant "Summary: …" since
                        we now show it as the headline above. */}
                    {it.evidence.length > 0 && (
                      <Group gap={4} wrap="wrap">
                        {it.evidence.filter((e) => !e.startsWith('Summary: ')).map((e, i) => {
                          const isAsk = e.startsWith('Ask: ') || e.startsWith('You owe:');
                          const isSuggest = e.startsWith('Suggested:');
                          const color = isAsk ? 'blue'
                            : isSuggest ? 'teal'
                            : STATE_META[activeState].color;
                          return (
                            <Tooltip key={`${it.message_id}-ev-${i}`} label="Why this is in this state">
                              <Badge
                                size="xs"
                                variant={isAsk || isSuggest ? 'filled' : 'light'}
                                color={color}
                                leftSection={<IconCheck size={9} />}
                              >
                                {e}
                              </Badge>
                            </Tooltip>
                          );
                        })}
                      </Group>
                    )}
                  </Stack>
                  <Stack gap={2} data-no-row-click>
                    <Tooltip label="Train: this should be in a different state">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTrainFor({ item: it, current: activeState });
                        }}
                      >
                        <IconSchool size={14} />
                      </ActionIcon>
                    </Tooltip>
                    {/* Round 28-U: one-click acknowledge. Permanent — "I
                        dealt with this, don't show me here again." Distinct
                        from snooze which is temporary. Stored as a
                        far-future snooze so the existing snooze-filter
                        machinery excludes it without schema changes. */}
                    <Tooltip label="Acknowledge — I dealt with this; never show again">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await api.addinActionInboxBulk({
                              message_ids: [it.message_id],
                              action: 'acknowledge',
                            });
                            notifications.show({ color: 'green', message: 'Acknowledged — removed from Action Inbox', autoClose: 2000 });
                            load();
                          } catch (err) {
                            notifications.show({
                              color: 'red',
                              message: err instanceof Error ? err.message : 'acknowledge failed',
                            });
                          }
                        }}
                      >
                        <IconCheck size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Stack>
                </Group>
              </Paper>
            ))}
          </Stack>
        </ScrollArea>
      )}

      {/* Train modal — single-item reclassification */}
      <Modal
        opened={!!trainFor}
        onClose={() => setTrainFor(null)}
        title="Train: where does this belong?"
        size="md"
      >
        {trainFor && (
          <Stack gap="sm">
            <Card padding="xs" withBorder>
              <Text size="sm" fw={500} lineClamp={2}>{trainFor.item.subject ?? '(no subject)'}</Text>
              <Text size="xs" c="dimmed">{trainFor.item.from_addr}</Text>
              <Group gap={4} mt={4}>
                {trainFor.item.evidence.map((e, i) => (
                  <Badge key={`tr-${i}`} size="xs" variant="light" color={STATE_META[trainFor.current].color}>
                    {e}
                  </Badge>
                ))}
              </Group>
            </Card>
            <Text size="sm">
              Currently in <Badge color={STATE_META[trainFor.current].color}>{STATE_META[trainFor.current].label}</Badge>.
              Where should it be?
            </Text>
            <SimpleGrid cols={2} spacing="xs">
              {(Object.keys(STATE_META) as ActionInboxState[])
                .filter((s) => s !== trainFor.current)
                .map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant="light"
                    color={STATE_META[s].color}
                    leftSection={STATE_META[s].icon}
                    onClick={async () => {
                      try {
                        await api.addinActionInboxTrain({
                          message_id: trainFor.item.message_id,
                          shown_in: trainFor.current,
                          says_belongs_in: s,
                        });
                        notifications.show({ color: 'green', message: `Trained → ${STATE_META[s].label}` });
                        setTrainFor(null);
                        // Round 28-AC: refetch so the trained row vanishes
                        // from the list immediately. Previously the snooze
                        // was created server-side but the UI showed stale
                        // data until the next manual reload.
                        await load();
                      } catch (e) {
                        notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
                      }
                    }}
                  >
                    {STATE_META[s].label}
                  </Button>
                ))}
            </SimpleGrid>
          </Stack>
        )}
      </Modal>

      {/* Round 28-M Phase 2.3: bulk AI ops result modal */}
      <Modal
        opened={!!bulkAiResult}
        onClose={() => setBulkAiResult(null)}
        title={
          bulkAiResult?.op === 'summarize' ? 'Bulk summary'
          : bulkAiResult?.op === 'draft_replies' ? `Drafted replies (${bulkAiResult?.count ?? 0})`
          : bulkAiResult?.op === 'extract_actions' ? 'Extracted action items'
          : 'AI result'
        }
        size="xl"
      >
        {bulkAiResult?.summary && (
          <ScrollArea h={500}>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{bulkAiResult.summary}</Text>
          </ScrollArea>
        )}
        {bulkAiResult?.drafts && (
          <Stack gap="sm">
            <ScrollArea h={500}>
              <Stack gap="sm">
                {bulkAiResult.drafts.map((d) => (
                  <Card key={d.message_id} withBorder padding="sm">
                    <Stack gap={4}>
                      <Text size="sm" fw={500} lineClamp={1}>{d.subject ?? '(no subject)'}</Text>
                      {d.error ? (
                        <Text size="xs" c="red">{d.error}</Text>
                      ) : (
                        <>
                          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{d.draft}</Text>
                          <Group gap="xs" justify="flex-end">
                            <Button size="compact-xs" variant="light"
                              onClick={() => navigator.clipboard.writeText(d.draft ?? '').then(() =>
                                notifications.show({ color: 'green', message: 'Copied' }),
                              )}>
                              Copy
                            </Button>
                          </Group>
                        </>
                      )}
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </ScrollArea>
          </Stack>
        )}
        {bulkAiResult?.actions ? (
          <ScrollArea h={500}>
            <Code block style={{ whiteSpace: 'pre-wrap' }}>
              {typeof bulkAiResult.actions === 'string' ? bulkAiResult.actions : JSON.stringify(bulkAiResult.actions, null, 2)}
            </Code>
          </ScrollArea>
        ) : null}
        {typeof bulkAiResult?.cost_usd === 'number' && bulkAiResult.cost_usd > 0 && (
          <Text size="xs" c="dimmed" mt="xs">cost: ${bulkAiResult.cost_usd.toFixed(4)}</Text>
        )}
      </Modal>

      {/* Round 28-U: row context menu (right-click on any item). Implemented
          as a plain fixed-position card so click delivery doesn't depend on
          Mantine's Portal+Target pairing (which had an issue: the 1×1 hidden
          target made `Open email` appear to do nothing on some screens). */}
      {rowMenu && (
        <>
          <div
            onClick={() => setRowMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); setRowMenu(null); }}
            style={{
              position: 'fixed', inset: 0, zIndex: 1000, background: 'transparent',
            }}
          />
          <Card
            padding={4}
            radius="sm"
            withBorder
            shadow="lg"
            style={{
              position: 'fixed',
              // Anchor to upper-left of click; clamp to viewport so the menu
              // never lands offscreen.
              left: Math.min(rowMenu.x, window.innerWidth - 280),
              top: Math.min(rowMenu.y, window.innerHeight - 280),
              width: 270,
              zIndex: 1001,
              background: 'var(--mantine-color-body)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Stack gap={0}>
              <Text size="xs" c="dimmed" px="sm" py={6} truncate>
                {rowMenu.item.subject || '(no subject)'}
              </Text>
              <Divider />
              <Button
                variant="subtle"
                size="sm"
                justify="start"
                leftSection={<IconMail size={14} />}
                onClick={() => {
                  const it = rowMenu.item;
                  // Round 28-U: visible feedback so we can tell if the click
                  // is actually reaching the handler. The COM addin's host
                  // bridge is the next leg; if this notification appears but
                  // Outlook never pops the email, it's the addin side.
                  const inWebView2 = !!(window as unknown as { chrome?: { webview?: unknown } })
                    .chrome?.webview;
                  notifications.show({
                    color: inWebView2 ? 'blue' : 'orange',
                    title: inWebView2 ? 'Asking Outlook to open…' : 'Not in addin (WebView2 not detected)',
                    message:
                      `${inWebView2 ? '' : 'No host bridge — running in plain browser? '}` +
                      `id: ${it.message_id.length > 60 ? `${it.message_id.slice(0, 57)}…` : it.message_id}`,
                    autoClose: 3000,
                  });
                  // 3.5.53: enriched bridge call — addin uses from_addr +
                  // received_at as composite DASL fallback when PR_INTERNET_
                  // MESSAGE_ID lookup misses (which it does on IMAP stores).
                  // 3.5.54: also passes folder so addin can walk just that
                  // folder per-item via PropertyAccessor when even composite
                  // DASL misses on cPanel IMAP.
                  openEmailInOutlook({
                    message_id: it.message_id,
                    from_addr: it.from_addr,
                    received_at: it.received_at,
                    subject: it.subject,
                    ...((it as { folder?: string | null }).folder !== undefined ? { folder: (it as { folder?: string | null }).folder ?? null } : {}),
                  });
                  setRowMenu(null);
                }}
              >
                Open email in Outlook
              </Button>
              <Button
                variant="subtle"
                size="sm"
                justify="start"
                leftSection={<IconUser size={14} />}
                onClick={() => {
                  onOpenSender?.(rowMenu.item.from_addr);
                  setRowMenu(null);
                }}
              >
                Open sender profile
              </Button>
              <Button
                variant="subtle"
                size="sm"
                justify="start"
                leftSection={<IconSchool size={14} />}
                onClick={() => {
                  setTrainFor({ item: rowMenu.item, current: activeState });
                  setRowMenu(null);
                }}
              >
                Train: change state
              </Button>
              <Divider my={4} />
              <Button
                variant="subtle"
                size="sm"
                justify="start"
                color="green"
                leftSection={<IconCheck size={14} />}
                onClick={async () => {
                  const ids = [rowMenu.item.message_id];
                  setRowMenu(null);
                  try {
                    await api.addinActionInboxBulk({ message_ids: ids, action: 'acknowledge' });
                    notifications.show({ color: 'green', message: 'Acknowledged — removed from Action Inbox' });
                    load();
                  } catch (e) {
                    notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'acknowledge failed' });
                  }
                }}
              >
                Acknowledge — never show again
              </Button>
              <Button
                variant="subtle"
                size="sm"
                justify="start"
                leftSection={<IconClock size={14} />}
                onClick={async () => {
                  const ids = [rowMenu.item.message_id];
                  setRowMenu(null);
                  try {
                    await api.addinActionInboxBulk({ message_ids: ids, action: 'snooze_30d' });
                    notifications.show({ color: 'blue', message: 'Snoozed for 30 days' });
                    load();
                  } catch (e) {
                    notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'snooze failed' });
                  }
                }}
              >
                Snooze 30 days (temporary)
              </Button>
              <Divider my={4} />
              <Text size="xs" c="dimmed" px="sm" py={2}>Remind me about this</Text>
              {([
                { label: 'In 1 hour', ms: 60 * 60 * 1000 },
                { label: 'Tomorrow 9 am', when: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
                { label: 'In 3 days', ms: 3 * 24 * 60 * 60 * 1000 },
                { label: 'Next week', ms: 7 * 24 * 60 * 60 * 1000 },
              ] as const).map((r) => (
                <Button key={r.label}
                  variant="subtle" size="sm" justify="start"
                  leftSection={<IconBell size={14} />}
                  onClick={async () => {
                    const item = rowMenu.item;
                    setRowMenu(null);
                    const remindAt = ('when' in r ? r.when() : new Date(Date.now() + r.ms)).toISOString();
                    try {
                      await api.addinReminderCreate({
                        message_id: item.message_id,
                        ...(item.subject != null ? { subject: item.subject } : {}),
                        remind_at: remindAt,
                        channel: 'inapp',
                      });
                      notifications.show({ color: 'green', message: `Reminder set for ${r.label.toLowerCase()}` });
                    } catch (e) {
                      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'reminder failed' });
                    }
                  }}>
                  {r.label}
                </Button>
              ))}
              <Divider my={4} />
              <Button
                variant="subtle"
                size="sm"
                justify="start"
                color="red"
                leftSection={<IconMailOff size={14} />}
                onClick={async () => {
                  const ids = [rowMenu.item.message_id];
                  const fromAddr = rowMenu.item.from_addr;
                  setRowMenu(null);
                  try {
                    await api.addinActionInboxBulk({ message_ids: ids, action: 'mute_sender' });
                    notifications.show({ color: 'gray', message: `Muted ${fromAddr}` });
                    load();
                  } catch (e) {
                    notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'mute failed' });
                  }
                }}
              >
                Mute sender
              </Button>
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
}

export function ActionInboxPanel({
  onSwitchTab,
  onOpenSender,
}: {
  onSwitchTab: (t: string) => void;
  onOpenSender?: (email: string) => void;
}): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinActionInbox>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      setData(await api.addinActionInbox());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load Action Inbox');
    } finally {
      setBusy(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load on mount only
  useEffect(() => {
    load();
  }, []);

  if (busy && !data) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }
  if (err) {
    return (
      <Alert color="red" variant="light">
        {err}
        <Button size="xs" onClick={load} ml="sm">
          Retry
        </Button>
      </Alert>
    );
  }
  if (!data) {
    return (
      <Alert color="yellow" variant="light">
        No data yet — index your mailboxes first.
      </Alert>
    );
  }

  const totalActions =
    data.must_answer_today.length +
    data.waiting_on_others.length +
    data.overdue_followups.length +
    data.business_risks.length +
    data.infrastructure_incidents.length;

  return (
    <Stack gap="md">
      {/* Phase 4: 5-state Action Inbox 2.0 — primary view. */}
      <ActionInbox5StatePanel {...(onOpenSender !== undefined ? { onOpenSender } : {})} />

      <Group justify="space-between">
        <Stack gap={0}>
          <Title order={4}>Classic view (legacy)</Title>
          <Text size="xs" c="dimmed">
            {totalActions} item{totalActions === 1 ? '' : 's'} surfaced from {data.noise_summary.machine_total_7d.toLocaleString()}{' '}
            machine emails grouped into {data.noise_summary.clusters_grouped} cluster
            {data.noise_summary.clusters_grouped === 1 ? '' : 's'}. Generated{' '}
            {formatRelative(data.generated_at)}.
          </Text>
        </Stack>
        <Button size="xs" variant="default" onClick={load} loading={busy}>
          Refresh
        </Button>
      </Group>

      {totalActions === 0 && (
        <Card padding="xl" radius="md" withBorder>
          <Stack align="center" gap={4}>
            <Text fw={600} size="lg">
              ✓ Inbox zero
            </Text>
            <Text size="sm" c="dimmed">
              Nothing requires your attention right now.
            </Text>
          </Stack>
        </Card>
      )}

      <MorningBriefV2Card />
      <MorningBriefBanner />
      <CloseoutCard />
      <PromisesSection />

      <ActionSection
        icon={<IconArrowBackUp {...ICON_PROPS} />}
        title="Must answer today"
        subtitle="Human emails from the last 48h needing a reply"
        accent="red"
        items={data.must_answer_today}
        onClickItem={(m) => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}
      />

      <ActionSection
        icon={<IconHistory {...ICON_PROPS} />}
        title="Waiting on others"
        subtitle="You sent the latest message in the thread; no reply yet"
        accent="orange"
        items={data.waiting_on_others}
        onClickItem={(m) => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}
      />

      {data.overdue_followups.length > 0 && (
        <ActionSection
          icon={<IconFlag {...ICON_PROPS} />}
          title={`Overdue follow-ups (>3 days)`}
          subtitle="Nudge or chase — these have been silent"
          accent="grape"
          items={data.overdue_followups}
          onClickItem={(m) => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}
        />
      )}

      <ActionSection
        icon={<IconShield {...ICON_PROPS} />}
        title="Business risks"
        subtitle="Finance / regulatory / legal / compliance signals"
        accent="violet"
        items={data.business_risks.map((b) => ({ ...b, label: b.risk }))}
        onClickItem={(m) => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}
      />

      {data.infrastructure_incidents.length > 0 && (
        <Card padding="md" radius="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-yellow-6)' }}>
          <Stack gap="xs">
            <Stack gap={0}>
              <Group gap="xs">
                <IconAlertTriangle {...ICON_PROPS} />
                <Text fw={600}>Infrastructure incidents</Text>
                <Badge>{data.infrastructure_incidents.length}</Badge>
              </Group>
              <Text size="xs" c="dimmed">
                Server / monitoring alerts grouped — open to see the underlying events
              </Text>
            </Stack>
            {data.infrastructure_incidents.map((c) => (
              <Paper
                key={c.cluster_key}
                p="sm"
                style={{
                  cursor: 'pointer',
                  borderLeft: `3px solid var(--mantine-color-${c.likely_severity === 'high' ? 'red' : c.likely_severity === 'medium' ? 'orange' : 'gray'}-5)`,
                }}
                onClick={() =>
                  postToHost({ type: 'open_in_outlook', message_id: c.message_ids[0] })
                }
              >
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={500} size="sm" lineClamp={1}>
                      {c.subject_sample}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {c.from} · first seen {formatRelative(c.first_seen)} · last{' '}
                      {formatRelative(c.last_seen)}
                    </Text>
                  </Stack>
                  <Group gap={4}>
                    <Badge color={c.likely_severity === 'high' ? 'red' : c.likely_severity === 'medium' ? 'orange' : 'gray'}>
                      {c.likely_severity}
                    </Badge>
                    <Badge variant="light">×{c.count}</Badge>
                  </Group>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Card>
      )}

      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between">
          <Stack gap={0}>
            <Group gap="xs">
              <IconArchive {...ICON_PROPS} />
              <Text fw={600} size="sm">
                Low-value noise
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {data.noise_summary.machine_total_7d.toLocaleString()} machine-generated emails in the
              last 7 days · {data.noise_summary.clusters_grouped} grouped into incidents above ·{' '}
              {data.noise_summary.singletons_remaining.toLocaleString()} ungrouped singletons
            </Text>
          </Stack>
          <Button size="xs" variant="subtle" onClick={() => onSwitchTab('search')}>
            View in Search →
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}

export function ActionSection<
  T extends {
    id: string;
    message_id: string;
    subject: string | null;
    from_addr?: string;
    from?: string;
    received_at: string;
    classification_tier?: string | null;
    label?: string;
  },
>({
  icon,
  title,
  subtitle,
  accent,
  items,
  onClickItem,
}: {
  icon: React.JSX.Element;
  title: string;
  subtitle: string;
  accent: string;
  items: T[];
  onClickItem: (m: T) => void;
}): React.JSX.Element {
  if (items.length === 0) return <></>;
  return (
    <Card
      padding="md"
      radius="md"
      withBorder
      style={{ borderLeft: `4px solid var(--mantine-color-${accent}-6)` }}
    >
      <Stack gap="xs">
        <Stack gap={0}>
          <Group gap="xs">
            {icon}
            <Text fw={600}>{title}</Text>
            <Badge color={accent}>{items.length}</Badge>
          </Group>
          <Text size="xs" c="dimmed">
            {subtitle}
          </Text>
        </Stack>
        <Stack gap={2}>
          {items.slice(0, 8).map((m) => {
            const fromAddr = m.from_addr ?? m.from ?? '';
            return (
              <Paper
                key={m.id}
                p="xs"
                style={{ cursor: 'pointer' }}
                onClick={() => onClickItem(m)}
              >
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={500} size="sm" lineClamp={1}>
                      {m.subject || '(no subject)'}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {fromAddr} · {shortDate(m.received_at)}
                    </Text>
                  </Stack>
                  <Group gap={4} onClick={(e) => e.stopPropagation()}>
                    {m.classification_tier && (
                      <Badge color={tierColor(m.classification_tier)} size="xs">
                        {m.classification_tier}
                      </Badge>
                    )}
                    {m.label && (
                      <Badge size="xs" variant="dot">
                        {m.label}
                      </Badge>
                    )}
                    <EmailRowMenu message_id={m.message_id} currentTier={m.classification_tier ?? null} />
                  </Group>
                </Group>
              </Paper>
            );
          })}
          {items.length > 8 && (
            <Text size="xs" c="dimmed" ta="center" mt={4}>
              +{items.length - 8} more
            </Text>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

// =========================================================================
// Morning brief banner — overnight overview. Round 40 point 40.
// Surfaces overnight volume, new P0s, security hits, overdue promises, SLAs.
// =========================================================================

